// ============================================================
// A vehicle plate's art (plan body-map P6) - what one traced vehicle is as
// data, and the reader that checks it at the boundary.
//
// Each is an asset in src/assets/plates/vehicle-<kind>.json, written by
// tools/plate-art/split_vehicles.py from the vehicle studies: the line art as
// a transform and path list, the three drawn regions (the Hull is whatever
// they leave, as a person's Body), the callout anchors, the callout rows and
// sides, and the view - the walker is seen from its front, the rest from
// above; and for its conditions (plan S2), the drawing's box, the parts they
// use and its blast. Same frame as the person figures (2123 x 3000, y down).
//
// Pure. Zero React dependency.
// ============================================================

import { VEHICLE_KINDS, type VehicleKind } from '../vehicleKind';
import { isRecord, pathGroup, type PathGroup } from './figureArt';

export type VehicleLocation = 'hull' | 'turret' | 'motive' | 'rear';
const DRAWN = ['turret', 'motive', 'rear'] as const;
type Point = [number, number];

export interface VehicleArt {
  id: string;
  kind: VehicleKind;
  name: string;
  /** From above (tank, skimmer, bike) or from the front (the walker): which way the facings sit round it. */
  view: 'top' | 'front';
  art: PathGroup;
  /** Each drawn location's polygon pieces ("x,y x,y ..."); the Hull is the rest. */
  regions: Record<(typeof DRAWN)[number], string[]>;
  anchors: Record<VehicleLocation, Point>;
  rows: Record<VehicleLocation, number>;
  sides: Record<VehicleLocation, 'L' | 'R'>;
  /**
   * The plate's stroke multiplier: its strokes are sized for art traced at a tenth of a unit (the person figures),
   * and the vehicles are stored at whole units (split_vehicles.py), so their lines take a tenth of the weight.
   */
  lineWeight: number;
  /** The drawing's box [x0, y0, x1, y1] - Helpless's one sight round the whole vehicle. */
  box: [number, number, number, number];
  parts: VehicleParts;
  /** Per location, the blast that Explodes throws it with; `spread` throws each of its pieces outward. */
  blow: Record<VehicleLocation, Blow>;
}

/**
 * The parts of a drawing its conditions use (frame units): the guns (Weapon Destroyed), the crew's hatches (Crew Hit),
 * the vision blocks and periscopes (Blinded), where smoke rises (Engine Damaged, On Fire), where the hull tears (Hull
 * Breach). Rects are [x0, y0, x1, y1], circles [cx, cy, r], points [x, y].
 */
interface VehicleParts {
  guns: [number, number, number, number][];
  hatches: [number, number, number][];
  hatchRects: [number, number, number, number][];
  vision: [number, number, number, number][];
  periscopes: [number, number, number][];
  smoke: Point[];
  breach: Point[];
}

export interface Blow {
  tx?: number;
  ty?: number;
  /** Degrees. */
  rot?: number;
  spread?: number;
}

const PART_NAMES = ['guns', 'hatches', 'hatchRects', 'vision', 'periscopes', 'smoke', 'breach'] as const;
const LOCATIONS: readonly VehicleLocation[] = ['hull', 'turret', 'motive', 'rear'];
const isBox = (v: unknown) => Array.isArray(v) && v.length === 4 && v.every((n) => typeof n === 'number');

const fail = (what: string): never => {
  throw new Error(`not vehicle art: ${what}`);
};

function regions(v: unknown): VehicleArt['regions'] {
  if (!isRecord(v)) return fail('regions');
  for (const id of DRAWN) if (!Array.isArray(v[id]) || (v[id] as unknown[]).length === 0) fail(`region ${id}`);
  return v as VehicleArt['regions'];
}

function parts(v: unknown): VehicleParts {
  if (!isRecord(v)) return fail('parts');
  for (const name of PART_NAMES) if (!Array.isArray(v[name])) fail(`part ${name}`);
  return v as unknown as VehicleParts;
}

/**
 * A vehicle asset read back, checked once at the boundary so the plate can trust it.
 * Throws a plain sentence naming what is wrong; never returns a partial vehicle.
 */
export function readVehicleArt(raw: unknown): VehicleArt {
  if (!isRecord(raw)) return fail('not an object');
  if (!VEHICLE_KINDS.includes(raw.kind as VehicleKind)) fail(`unknown kind ${String(raw.kind)}`);
  if (raw.view !== 'top' && raw.view !== 'front') fail(`unknown view ${String(raw.view)}`);
  if (!(typeof raw.lineWeight === 'number' && raw.lineWeight > 0)) fail('lineWeight');
  if (!isBox(raw.box)) fail('box');
  if (!isRecord(raw.blow) || !LOCATIONS.every((id) => isRecord((raw.blow as Record<string, unknown>)[id])))
    fail('blow');
  const art = pathGroup(raw.art, 'art');
  return { ...(raw as unknown as VehicleArt), art, regions: regions(raw.regions), parts: parts(raw.parts) };
}
